/**
 * Plugin detail — per-plugin sub-page at /plugins/:kind/:name.
 *
 * Replaces the kebab config popups: a capability shows its Global init
 * defaults and how to create a task through the API as tabs, and an
 * Environment card that edits what its environment carries; tools show metadata only,
 * with no install state, because a tool is carried inside every capability
 * that declares it and is never installed on its own; a tool's page names
 * those capabilities (Feature 87, D2).
 * A page head over a two-column (main / aside) layout. Its action and More
 * actions menu come from `usePluginActions`: a capability is uninstalled and
 * deleted from here (Feature 87, D1).
 *
 * An infra plugin (Feature 84) is installed by hand or by the platform, and
 * boots only move it forward (Feature 87, D4): its page shows the mark and
 * the environment whose catalog it follows, offers Release with Reinstall
 * newest, Disable, and Enable in its menu, never Uninstall or Delete plugin,
 * and edits each capability it ships at the global layer only.
 * An installed capability's Configuration starts with its priority (optum-agent
 * Feature 87, D10). A distribution that
 * ships several capabilities gets one configuration tab per capability id.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';

import { getCatalogEntry } from '@optum/api-client/endpoints/catalog';
import type { CatalogEntryOut, CatalogKind } from '@optum/api-client/types';

import { EmptyBlock } from '../../ui/AsyncState.js';
import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { Badge, KeyValue, Typography } from '../../ui/finixt.js';
import { qk } from '../../lib/query-keys.js';
import { RecordShape, ShapeError, ShapeLoading } from '../../ui/shapes.js';
import { CapabilityApiGuide } from './CapabilityApiGuide.js';
import { CapabilityPriorityField } from './CapabilityPriorityField.js';
import { EnvironmentCard } from './EnvironmentCard.js';
import { ExecutionServerCard } from './ExecutionServerCard.js';
import { VersionsSection } from './VersionsSection.js';
import { ResizableColumns } from './ResizableColumns.js';
import { ToolCarriersCard } from './ToolCarriersCard.js';
import { usePluginActions } from './usePluginActions.js';
import { ConfigLayerRail, type EditableLayer } from './ConfigLayerRail.js';
import {
  CapabilityGlobalInitContent,
  PartnerCapabilityInitContent,
} from './PluginConfigDialogs.js';

const KINDS: readonly CatalogKind[] = ['capabilities', 'tools'];
const KIND_LABEL: Record<CatalogKind, string> = {
  capabilities: 'Capability',
  tools: 'Tool',
};

type ConfigTab = 'init' | 'api';

const CAPABILITY_TABS: TabItem<ConfigTab>[] = [
  { value: 'init', label: 'Global init defaults', icon: 'tune' },
  { value: 'api', label: 'Create via API', icon: 'terminal' },
];
// An infra capability's tasks come from its own routes; POST /tasks refuses
// it, so there is no API guide to show.
const INFRA_TABS: TabItem<ConfigTab>[] = [CAPABILITY_TABS[0]!];

/** The capability ids to configure: what the installed plugin ships, else its name. */
function configurableIds(entry: CatalogEntryOut): string[] {
  return entry.capability_ids.length > 0 ? entry.capability_ids : [entry.name];
}

/** The infra facts the page reads: the channel and the installed status. */
function infraFacts(entry: CatalogEntryOut) {
  return [
    {
      label: 'Kept current',
      value: `by the platform, from the ${entry.channel ?? '—'} index`,
      mono: false,
    },
    { label: 'Status', value: entry.installed_status ?? 'not installed yet', mono: false },
  ];
}
// One remembered width for every plugin's detail page; the environment
// column is the same column whichever plugin is open.
const ASIDE_WIDTH_KEY = 'optum.plugin-detail.aside-width';

export function PluginDetail() {
  const [configTab, setConfigTab] = useState<ConfigTab>('init');
  // `?capability=` opens one capability's editor, so Runtime settings can
  // link to each ingestion capability (Feature 84, D19).
  const [searchParams] = useSearchParams();
  const [configCapability, setConfigCapability] = useState<string | null>(
    searchParams.get('capability'),
  );
  const [configLayer, setConfigLayer] = useState<EditableLayer>('global');
  const [configPartnerId, setConfigPartnerId] = useState<string | null>(null);
  const { kind = '', name = '' } = useParams<{ kind: string; name: string }>();
  const isValidKind = (KINDS as readonly string[]).includes(kind);
  const catalogKind = kind as CatalogKind;

  useBreadcrumbTail(name);

  const query = useQuery({
    queryKey: qk.catalogEntry(catalogKind, name),
    queryFn: () => getCatalogEntry(catalogKind, name),
    enabled: isValidKind,
  });

  // The header's action and menu: Release, Uninstall, Uninstall and delete,
  // Delete, or the infra plugin's own (Feature 87, D1, D2).
  const actions = usePluginActions(catalogKind, name, query.data);

  if (!isValidKind) return <Navigate to="/plugins" replace />;

  if (query.isPending) {
    return <ShapeLoading shape="record" />;
  }
  if (query.isError) {
    return <ShapeError shape="record" error={query.error} fallback="Failed to load" />;
  }

  const entry = query.data;
  const capabilityIds = configurableIds(entry);
  const capabilityId =
    configCapability && capabilityIds.includes(configCapability)
      ? configCapability
      : capabilityIds[0]!;

  return (
    <RecordShape
      back={{ to: '/plugins', label: 'Plugins' }}
      eyebrow={
        <>
          <Typography variant="eyebrow">{KIND_LABEL[catalogKind]}</Typography>
          {entry.is_infra ? (
            <Badge tone="violet" shape="square">
              Infra
            </Badge>
          ) : null}
          {catalogKind !== 'tools' ? (
            <Badge tone={entry.installed_version ? 'ok' : 'neutral'} mono shape="square">
              {entry.installed_version ? `installed v${entry.installed_version}` : 'not installed'}
            </Badge>
          ) : null}
          {entry.installed_status === 'disabled' ? (
            <Badge tone="warn" shape="square">
              disabled
            </Badge>
          ) : null}
        </>
      }
      title={entry.name}
      subtitle={entry.summary ?? undefined}
      action={actions.action}
      menu={actions.menu}
    >
      {actions.dialog}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 30, paddingBottom: 64 }}>
        {/* A tool has no configuration at any layer — it is a dependency other
          plugins pull in. An empty Configuration card saying so still reads as
          a form you have not filled in yet, so the card is absent instead and
          the page is the tool's metadata, which is all there is to know. */}
        <ResizableColumns
          storageKey={ASIDE_WIDTH_KEY}
          main={
            <>
              {catalogKind !== 'tools' && (
                <SectionCard
                  title="Configuration"
                  subtitle={
                    entry.is_infra
                      ? 'Global init defaults for each capability this plugin ships.'
                      : 'Global init defaults for this capability, and how to create a task with it.'
                  }
                >
                  <SectionCardBody style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {capabilityIds.length > 1 && (
                      <Tabs
                        items={capabilityIds.map((id) => ({ value: id, label: id }))}
                        value={capabilityId}
                        onChange={setConfigCapability}
                      />
                    )}
                    {catalogKind === 'capabilities' && entry.installed_version !== null && (
                      <CapabilityPriorityField key={capabilityId} capabilityId={capabilityId} />
                    )}
                    <Tabs
                      items={entry.is_infra ? INFRA_TABS : CAPABILITY_TABS}
                      value={entry.is_infra ? 'init' : configTab}
                      onChange={setConfigTab}
                    />
                    {configTab === 'init' && (
                      <ConfigLayerRail
                        layer={entry.is_infra ? 'global' : configLayer}
                        onLayerChange={setConfigLayer}
                        partnerId={configPartnerId}
                        onPartnerChange={setConfigPartnerId}
                        infra={entry.is_infra}
                      />
                    )}
                    {catalogKind === 'capabilities' && (
                      <>
                        {(entry.is_infra || (configTab === 'init' && configLayer === 'global')) && (
                          <CapabilityGlobalInitContent
                            key={capabilityId}
                            capabilityId={capabilityId}
                          />
                        )}
                        {!entry.is_infra &&
                          configTab === 'init' &&
                          configLayer === 'partner' &&
                          (configPartnerId ? (
                            <PartnerCapabilityInitContent
                              partnerId={configPartnerId}
                              capabilityId={capabilityId}
                            />
                          ) : (
                            <EmptyBlock>
                              Choose a federated partner to edit its defaults.
                            </EmptyBlock>
                          ))}
                        {!entry.is_infra && configTab === 'api' && (
                          <CapabilityApiGuide capabilityName={capabilityId} />
                        )}
                      </>
                    )}
                  </SectionCardBody>
                </SectionCard>
              )}
              {catalogKind === 'tools' && <ToolCarriersCard carriers={entry.carried_by} />}
              <VersionsSection kind={catalogKind} entry={entry} />
            </>
          }
          aside={
            <>
              {catalogKind !== 'tools' && (
                <>
                  <ExecutionServerCard
                    pluginName={entry.name}
                    installedVersion={entry.installed_version}
                    restarted={{ at: entry.server_restarted_at, by: entry.server_restarted_by }}
                  />
                  <EnvironmentCard
                    pluginName={entry.name}
                    installedVersion={entry.installed_version}
                  />
                </>
              )}

              <SectionCard title="Metadata">
                <SectionCardBody>
                  <KeyValue
                    items={[
                      { label: 'Name', value: entry.name },
                      { label: 'Kind', value: KIND_LABEL[catalogKind], mono: false },
                      ...(catalogKind !== 'tools'
                        ? [{ label: 'Installed', value: entry.installed_version ?? '—' }]
                        : []),
                      ...(entry.is_infra ? infraFacts(entry) : []),
                      { label: 'Latest', value: entry.latest_version },
                      ...(catalogKind !== 'tools'
                        ? [
                            {
                              label: 'Upgrade',
                              value: entry.upgrade_available ? 'Available' : 'Up to date',
                              mono: false,
                            },
                          ]
                        : []),
                    ]}
                  />
                </SectionCardBody>
              </SectionCard>
            </>
          }
        />
      </div>
    </RecordShape>
  );
}
